import React, { useState } from 'react';
import { Alert, Button, Card, Descriptions, Space, Spin, Tabs, Tag, Typography, Popconfirm, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../ui';
import { useReferenceCardOverlay } from '../../hooks/useReferenceCardOverlay';
import { EntityCardOverlayFrame } from '../../components/entityCard/EntityCardOverlayFrame';
import useHasPermission from '../../hooks/useHasPermission';
import { useGetVehicleByIdQuery, useDeleteVehicleMutation, type Vehicle } from '../../store/api/referencesApi';
import { vehicleCategoryLabel } from '../../constants/vehicleCategory';
import CreateVehicleModal from '../../components/common/CreateVehicleModal';
import EntityDocumentsBlock from '../../components/documents/EntityDocumentsBlock';
import '../contractors/ContractorCardPage.css';

const { Text } = Typography;

const EM_DASH = '—';

function formatDdMmYyyy(iso?: string | null): string {
  if (iso == null || String(iso).trim() === '') return EM_DASH;
  const d = new Date(iso as string);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const cardSurface: React.CSSProperties = {
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: 8
};

const sectionTitle = (t: string) => <Typography.Title level={5} style={{ margin: 0 }}>{t}</Typography.Title>;

const VehicleCardContent: React.FC = () => {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const idNum = idParam ? parseInt(idParam, 10) : NaN;
  const skip = Number.isNaN(idNum);
  const { isOverlay, close } = useReferenceCardOverlay();

  const canViewReferences = useHasPermission('references.view');
  const canEditReferences = useHasPermission('references.edit');
  const canCreateReferences = useHasPermission('references.create');
  const canModify = canEditReferences || canCreateReferences;

  const { data, isLoading, isError, error, refetch } = useGetVehicleByIdQuery(idNum, {
    skip: skip || !canViewReferences
  });
  const [deleteVehicle] = useDeleteVehicleMutation();
  const [editOpen, setEditOpen] = useState(false);

  if (!canViewReferences) {
    return (
      <PageLayout title="Транспорт" titleLevel={2}>
        <Card>
          <Text>У вас нет прав для просмотра справочников.</Text>
        </Card>
      </PageLayout>
    );
  }

  if (skip) {
    return (
      <PageLayout title="Транспорт" titleLevel={2}>
        <Alert type="error" message="Некорректная ссылка" showIcon />
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout title="Транспорт" titleLevel={2}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !data) {
    const msg =
      (error as { data?: { error?: string } })?.data?.error ||
      'Не удалось загрузить ТС. Возможно, запись удалена или недоступна.';
    return (
      <PageLayout title="Транспорт" titleLevel={2}>
        <Alert type="error" message={msg} showIcon />
      </PageLayout>
    );
  }

  const v = data as Vehicle;

  return (
    <div className="contractor-card-page">
      <PageLayout
        titleLevel={2}
        title={v.registrationNumber}
        subtitle={
          <Text type="secondary">
            {vehicleCategoryLabel(v.vehicleCategory || 'TRUCK')}
            {v.brand ? ` · ${v.brand}` : ''}
          </Text>
        }
        actions={
          <Space>
            {isOverlay && (
              <Button type="default" onClick={close}>
                Закрыть
              </Button>
            )}
            {canModify && (
              <>
                <Button type="primary" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                  Редактировать
                </Button>
                <Popconfirm
                  title="Удалить ТС?"
                  description="Действие нельзя отменить"
                  onConfirm={async () => {
                    try {
                      await deleteVehicle(v.id).unwrap();
                      message.success('ТС удалено');
                      if (isOverlay) close();
                      else navigate('/references');
                    } catch (e: unknown) {
                      message.error((e as { data?: { error?: string } })?.data?.error || 'Ошибка удаления');
                    }
                  }}
                >
                  <Button danger>Удалить</Button>
                </Popconfirm>
              </>
            )}
          </Space>
        }
      >
        <div className="contractor-card-grid">
          <Card
            title={sectionTitle('Паспорт')}
            size="small"
            styles={{ body: { padding: '12px 14px' } }}
            style={cardSurface}
          >
            <Descriptions column={1} size="small" colon>
              <Descriptions.Item label="Тип">
                <Tag color={v.vehicleCategory === 'TRAILER' ? 'purple' : 'geekblue'}>
                  {vehicleCategoryLabel(v.vehicleCategory || 'TRUCK')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Госномер">
                <Text strong>{v.registrationNumber}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Марка">{v.brand?.trim() || EM_DASH}</Descriptions.Item>
              <Descriptions.Item label="ТС компании">
                <Tag color={v.isCompanyVehicle ? 'blue' : 'default'}>
                  {v.isCompanyVehicle ? 'Да' : 'Нет'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Создано">{formatDdMmYyyy(v.createdAt)}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title={sectionTitle('Характеристики')}
            size="small"
            styles={{ body: { padding: '12px 14px' } }}
            style={cardSurface}
          >
            <Descriptions column={1} size="small" colon>
              {v.vehicleCategory === 'TRAILER' ? (
                <Descriptions.Item label="Тип прицепа">{v.vehicleType || EM_DASH}</Descriptions.Item>
              ) : (
                <>
                  <Descriptions.Item label="Колёсная формула">{v.wheelFormula || EM_DASH}</Descriptions.Item>
                  <Descriptions.Item label="Назначение / тип">{v.vehicleType || EM_DASH}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Грузоподъёмность, кг">
                {v.carryingCapacity != null && !Number.isNaN(v.carryingCapacity)
                  ? v.carryingCapacity
                  : EM_DASH}
              </Descriptions.Item>
              <Descriptions.Item label="Объём, м³">
                {v.volumeM3 != null && !Number.isNaN(v.volumeM3) ? v.volumeM3 : EM_DASH}
              </Descriptions.Item>
              <Descriptions.Item label="Габариты">{v.dimensionsText || EM_DASH}</Descriptions.Item>
            </Descriptions>
          </Card>
        </div>

        <Card
          size="small"
          style={{ marginTop: '1.25rem', ...cardSurface }}
          styles={{ body: { padding: '12px 14px' } }}
        >
          <Tabs
            items={[
              {
                key: 'drivers',
                label: 'Закреплённые водители',
                children: v.driversDefault && v.driversDefault.length > 0 ? (
                  <Space size={[8, 8]} wrap>
                    {v.driversDefault.map((d) => (
                      <Link key={d.id} to={`/drivers/${d.id}`}>
                        {d.fullName}
                      </Link>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">{EM_DASH}</Text>
                )
              },
              {
                key: 'documents',
                label: 'Документы',
                children: (
                  <EntityDocumentsBlock
                    entityType="vehicle"
                    entityId={v.id}
                    canModify={canModify}
                    documentDetails={{
                      VEHICLE_REGISTRATION: { number: v.registrationNumber }
                    }}
                  />
                )
              }
            ]}
          />
        </Card>
      </PageLayout>

      {canModify && (
        <CreateVehicleModal
          isOpen={editOpen}
          vehicle={v}
          onClose={() => {
            setEditOpen(false);
            void refetch();
          }}
          onVehicleCreated={() => {
            setEditOpen(false);
            void refetch();
          }}
        />
      )}
    </div>
  );
};

const VehicleCardPage: React.FC = () => (
  <EntityCardOverlayFrame>
    <VehicleCardContent />
  </EntityCardOverlayFrame>
);

export default VehicleCardPage;
